import { useCallback, useEffect, useRef, useState } from 'react';
import { apiClient } from '@/lib/api-client';
import {
  isFinanceDashboard,
  isFinanceMovementList,
  type DirectionFilter,
  type FinanceState,
  type StatusFilter,
} from './dashboard-model';

export type DashboardFilters = {
  direction: DirectionFilter;
  status: StatusFilter;
  category: string;
  projectLabel: string;
};

function buildQuery(year: number, filters: DashboardFilters) {
  return {
    year,
    ...(filters.direction === 'all' ? {} : { direction: filters.direction }),
    ...(filters.status === 'all' ? {} : { movementStatus: filters.status }),
    ...(filters.category === 'all' ? {} : { category: filters.category }),
    ...(filters.projectLabel === 'all' ? {} : { projectLabel: filters.projectLabel }),
  };
}

async function fetchReadyState(
  year: number,
  filters: DashboardFilters,
): Promise<Extract<FinanceState, { status: 'ready' }>> {
  const query = buildQuery(year, filters);

  const [dashboardResult, movementResult] = await Promise.all([
    apiClient.GET('/v1/finance/dashboard', { params: { query } }),
    apiClient.GET('/v1/finance/movements', { params: { query } }),
  ]);

  const dashboardPayload: unknown = dashboardResult.data;
  if (
    dashboardResult.error !== undefined ||
    !dashboardResult.response.ok ||
    !isFinanceDashboard(dashboardPayload)
  ) {
    throw new Error(`finance dashboard ${year} failed`);
  }

  const movementPayload: unknown = movementResult.data;
  if (
    movementResult.error !== undefined ||
    !movementResult.response.ok ||
    !isFinanceMovementList(movementPayload)
  ) {
    throw new Error(`finance movements ${year} failed`);
  }

  return {
    status: 'ready',
    dashboard: dashboardPayload,
    movements: movementPayload.movements,
    availableCategories: movementPayload.availableCategories,
    availableProjectLabels: movementPayload.availableProjectLabels,
    refreshing: false,
  };
}

export function useDashboardData(selectedYear: number) {
  const [directionFilter, setDirectionFilter] = useState<DirectionFilter>('all');
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('all');
  const [categoryFilter, setCategoryFilter] = useState<string>('all');
  const [projectLabelFilter, setProjectLabelFilter] = useState<string>('all');
  const [state, setState] = useState<FinanceState>({ status: 'loading' });

  useEffect(() => {
    setDirectionFilter('all');
    setStatusFilter('all');
    setCategoryFilter('all');
    setProjectLabelFilter('all');
  }, [selectedYear]);

  // Monotonic request token. Two filter clicks in quick succession race, and without this the first
  // response can land after the second — leaving the register showing rows that do not match the
  // selection on screen. Stale rows presented as current are a failed read reported as a successful
  // one, which `FIN-FR-19` forbids.
  const requestTokenRef = useRef(0);

  const load = useCallback(async () => {
    const token = requestTokenRef.current + 1;
    requestTokenRef.current = token;

    // Refresh in place when there is already something to show, instead of blanking the screen. The
    // previous implementation set `loading` unconditionally, so the control the user had just clicked
    // unmounted itself and focus was lost.
    setState((current) =>
      current.status === 'ready' ? { ...current, refreshing: true } : { status: 'loading' },
    );

    try {
      const ready = await fetchReadyState(selectedYear, {
        direction: directionFilter,
        status: statusFilter,
        category: categoryFilter,
        projectLabel: projectLabelFilter,
      });

      if (token !== requestTokenRef.current) {
        return;
      }

      setState(ready);
    } catch {
      // A superseded request must not replace a newer view with an error either.
      if (token !== requestTokenRef.current) {
        return;
      }
      setState({ status: 'error' });
    }
  }, [categoryFilter, directionFilter, projectLabelFilter, selectedYear, statusFilter]);

  useEffect(() => {
    void load();
  }, [load]);

  return {
    state,
    load,
    directionFilter,
    setDirectionFilter,
    statusFilter,
    setStatusFilter,
    categoryFilter,
    setCategoryFilter,
    projectLabelFilter,
    setProjectLabelFilter,
  };
}
